/**
 * @format
 * @flow strict-local
 */

import '@/injectWebCss';
import 'react-native-get-random-values';
import "setimmediate";

import { ThemeProvider } from '@emotion/react';
import { DrawerNavigationOptions, createDrawerNavigator } from '@react-navigation/drawer';
import { NavigationContainer, useIsFocused } from '@react-navigation/native'; // Added useIsFocused
import React, { FC } from 'react';
import { GestureHandlerRootView as GestureHandlerWrapper } from 'react-native-gesture-handler';
import { DefaultTheme, Provider as PaperProvider } from 'react-native-paper';
import { AlertsProvider } from 'react-native-paper-alerts';


import { Drawer as DrawerContent } from '@components/3-organisms';
import * as Pages from '@components/5-pages';
import { EXPO_PUBLIC_PAYBOOKS_GOOGLE_WEB_CLIENT_ID, RootParamList, STATE } from '@lib/constants';

import styled from '@emotion/native';
import setTheme from '@lib/theme';

import { useBusiness } from "@lib/hooks/useBusiness";
import { useFeedInstitutionItemsUpdate, useFeedRefresh } from '@lib/hooks/useFeed';
import { useUser } from "@lib/hooks/useUser";
import { useUserSubscriptions } from "@lib/hooks/useUserSubscription";

import { GoogleOAuthProvider } from '@lib/hooks/useGoogleAuth';
import { mixpanel } from "@lib/mixpanel";
import subscription from '@lib/subscription';

const GestureHandlerRootView = styled(GestureHandlerWrapper)`flex: 1;`;
const Drawer = createDrawerNavigator<RootParamList>();
const theme = setTheme('light');


// TODO:  REFACTOR THE APP CODE TO PROPERLY MANAGE THROUGH REACT HOOK (`useSate`)
// THE SCREEN (PAGE) DATA THAT NEED TO BE REFRESHED AND COMPONENT PROPERTIES THAT
// NEED TO BE RESET UPON TRANSITIONING BACK TO A SCREEN

// THIS CODE PATCHES THE DEPRECATED `unmountOnBlur` REACT-NAVIGATION PROP
// THIS IS A BAD PRACTICE AS SCREEN TRANSITIONS CAN BE SLUGGISH / FLICKISH
// IT GOES AGAINT `react-navigation` COMMUNITY GUIDELINES

// QUICK-FIX / PATCH: High-order wrapper engine to inject unmount execution
// This runs once when the module loads, avoiding invalid hook/render loops.
const createUnmountedPages = (pagesNamespace: any) => {
  const wrapped: any = {};
  Object.keys(pagesNamespace).forEach((key) => {
    const RawScreenComponent = pagesNamespace[key];

    // Return a functional component that listens directly to React Navigation's visibility tree
    wrapped[key] = (props: any) => {
      const isFocused = useIsFocused();
      return isFocused ? <RawScreenComponent {...props} /> : null;
    };
  });
  return wrapped;
};
// Generate your safe, self-destructing page configurations
const SafePages = createUnmountedPages(Pages);
// Removed the deprecated unmountOnBlur property here
const screenOptions: DrawerNavigationOptions = { headerShown: false, drawerType: 'front' };

const App: FC<{}> = () => {

  const SPLASH_SCREEN_TIMEOUT = 1.5 * 1000;
  const [isPlashScreen, setIsSplashScreen] = React.useState(true);
  const [isOnboardingSlider,] = React.useState(true);

  const [user,] = useUser();
  const [business,] = useBusiness();
  const [userSubscription,] = useUserSubscriptions();
  const [feedInstitutionItems,] = useFeedInstitutionItemsUpdate();
  const [sync] = useFeedRefresh();

  React.useEffect(() => {
    setTimeout(() => setIsSplashScreen(false), SPLASH_SCREEN_TIMEOUT);
  }, [user, business, userSubscription]);

  React.useEffect(() => {
    const initializeServices = async () => {
      if (user.state === STATE.DONE) {
        await subscription.initialize(`${user.data.id}`);
        mixpanel.init().then(() => mixpanel.identify((user?.data?.id).toString()));
        const profile = { "\$id": user?.data?.id, "\$email": user?.data?.email };
        mixpanel.setPeople(profile);
      } else {
        mixpanel.init().then(() => mixpanel.identify('0'));
      }
    };
    initializeServices();
  }, [user]);

  return (
    <GestureHandlerRootView>
      <GoogleOAuthProvider clientId={EXPO_PUBLIC_PAYBOOKS_GOOGLE_WEB_CLIENT_ID}>
        <PaperProvider theme={{ ...DefaultTheme, roundness: 1 }}>
          <AlertsProvider>
            <ThemeProvider theme={theme}>
              <NavigationContainer onStateChange={mixpanel.onReactNavigationStateChange}>
                <Drawer.Navigator backBehavior={"history"} screenOptions={screenOptions} drawerContent={props => <DrawerContent {...props} />} >

                  {(() => {
                    if (isPlashScreen) {
                      // 2. Swapped "Pages." namespaces to "SafePages." to force the conditional unmount behavior
                      return <Drawer.Screen name='SplashScreen' component={SafePages.SplashScreen} />;
                    }
                    if (!user?.data) {
                      return (<>
                        <Drawer.Screen name='OnboardingSlider' component={SafePages.OnboardingSlider} />
                        <Drawer.Screen name='EmailLogin' component={SafePages.EmailLogin} />
                        <Drawer.Screen name='OtpLogin' component={SafePages.OtpLogin} />
                      </>);
                    }
                    if (!business?.data) {
                      return (<>
                        <Drawer.Screen name='BusinessCreate' component={SafePages.BusinessCreate} />
                      </>);
                    }
                    if (!userSubscription?.data) {
                      return (<>
                        <Drawer.Screen name='Subscriptions' component={SafePages.Subscriptions} />
                      </>);
                    }
                    else {
                      return (<>
                        <Drawer.Screen name='Dashboard' component={SafePages.Dashboard} />

                        <Drawer.Screen name='Contacts' component={SafePages.Contacts} />
                        <Drawer.Screen name='ContactView' component={SafePages.ContactView} />
                        <Drawer.Screen name='ContactCreate' component={SafePages.ContactCreate} />

                        <Drawer.Screen name='ChartOfAccounts' component={SafePages.Accounts} />
                        <Drawer.Screen name='AccountCreate' component={SafePages.AccountCreate} />

                        <Drawer.Screen name='ProductsServices' component={SafePages.ProductsServices} />
                        <Drawer.Screen name='NonInventoryProductServiceCreate' component={SafePages.NonInventoryProductServiceCreate} />
                        <Drawer.Screen name='InventoryProductCreate' component={SafePages.InventoryProductCreate} />

                        <Drawer.Screen name='Documents' component={SafePages.Documents} />
                        <Drawer.Screen name='EstimateCreate' component={SafePages.EstimateCreate} />
                        <Drawer.Screen name='DocumentView' component={SafePages.DocumentView} />

                        <Drawer.Screen name='StatementIncome' component={SafePages.StatementIncome} />
                        <Drawer.Screen name='StatementBalanceSheet' component={SafePages.StatementBalanceSheet} />

                        <Drawer.Screen name='Taxes' component={SafePages.Taxes} />

                        <Drawer.Screen name='More' component={SafePages.More} />
                      </>);
                    }
                  })()}

                </Drawer.Navigator>
              </NavigationContainer>
            </ThemeProvider>
          </AlertsProvider>
        </PaperProvider>
      </GoogleOAuthProvider>
    </GestureHandlerRootView>
  );
};

export default App;